import { Video, VideoVersion } from '@src/types.ts/story';
import {
  VideoResourceMetadata,
  VideoResources,
  VideoSource,
} from '@src/types.ts/video';

/**
 * Fetches a video source from a specific URL
 *
 * @param videoSourceUrl URL to fetch video source from
 * @returns VideoSource json
 */
async function fetchJsonResourceFromUrl(
  resourceUrl: string,
): Promise<Record<string, any>> {
  const jsonResponse = await fetch(resourceUrl);
  const videoSourceJson = (await jsonResponse.json()) as VideoSource;
  return videoSourceJson;
}

export async function requestVideoResources(
  storyId: string,
  videoId: string,
  videoVersionId: string | 'latest',
  fields: ('videoSourceSnapshot' | 'transcriptionSnapshot')[],
): Promise<VideoResources> {
  const query = new URLSearchParams({
    storyId,
    videoId,
    versionId: videoVersionId,
    fields: fields.join(','),
  }).toString();
  const url = `${process.env.REACT_APP_API_URL}/api/video/version-resources?${query}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (response.ok) {
    const responseJson = await response.json();
    if (responseJson.data) {
      return responseJson.data as VideoResources;
    } else {
      throw new Error('Response data is empty');
    }
  } else {
    console.error('Failed to fetch cloudfront links');
    return {};
  }
}

export async function populateVideoVersionWithResources<
  V extends Pick<Video, 'id'> & {
    videoSource?: Video['videoSource'];
    videoJson?: Video['videoSource'];
    versionId?: string;
  },
>(
  storyId: string,
  video: V,
  versionId: string | 'latest' = video.versionId || 'latest',
): Promise<V & { videoSource: Video['videoSource'] }> {
  if (video.videoSource) {
    console.log(
      'VideoSourceProvider | video source already populated, skipping fetch',
    );
    return video as V & { videoSource: Video['videoSource'] };
  }

  if (video.videoJson) {
    console.log('VideoSourceProvider | taking from videoJson');
    video.videoSource = video.videoJson;
    return video as V & { videoSource: Video['videoSource'] };
  }

  const resources = await requestVideoResources(storyId, video.id!, versionId, [
    'videoSourceSnapshot',
    'transcriptionSnapshot',
  ]);
  if (resources.videoSourceSnapshot) {
    const videoSource = await fetchJsonResourceFromUrl(
      resources.videoSourceSnapshot?.url,
    );
    video.videoSource = videoSource;
    return video as V & { videoSource: Video['videoSource'] };
  } else {
    throw new Error(
      `Cannot load video source for video ${video.id} in story ${storyId}`,
    );
  }
}

export async function saveVideoResource(
  storyId: string,
  resource: object,
  resourceField: 'videoSourceSnapshot' | 'transcriptionSnapshot',
): Promise<VideoResourceMetadata | null> {
  const url = `${process.env.REACT_APP_API_URL}/api/video/version-resource`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      storyId,
      resourceContentJson: JSON.stringify(resource, null, 2),
      resourceField,
    }),
  });
  if (response.ok) {
    return (await response.json()).data as VideoResourceMetadata;
  } else {
    console.error('Failed to save video resource', response.statusText);
    console.error('Response body:', await response.text());
    return null;
  }
}
